import React from 'react';
import { Search, FileText, AlertTriangle } from 'lucide-react';
import { Transaction } from '../../../types/transaction.types';
import { formatCurrency } from '../../../utils/currency';
import { SearchResultsSkeleton } from './SearchResultsSkeleton';
import { DIAS_DEVOLUCION, diasDesdeLaCompra, fueraDePlazo } from '../../../constants/returns.constants';

interface InvoiceSearchResultsProps {
  searchQuery: string;
  invoice: Transaction | null;
  isLoading: boolean;
  error: string | null;
  onSelectInvoice: (invoice: Transaction) => void;
}

/**
 * Resultado del buscador en devoluciones: lo tecleado se manda tal cual como id
 * de factura, asi que hay como mucho un resultado. Reutiliza las clases de
 * SearchResults para no duplicar estilos.
 */
export const InvoiceSearchResults: React.FC<InvoiceSearchResultsProps> = ({
  searchQuery,
  invoice,
  isLoading,
  error,
  onSelectInvoice,
}) => {
  const term = searchQuery.trim();

  if (!term) {
    return (
      <div className="results-content custom-scrollbar">
        <div className="results-empty">
          <div className="results-empty-icon-wrapper">
            <Search className="results-empty-icon" />
          </div>
          <p className="results-empty-text-small">Escribe el número del ticket o de la factura</p>
        </div>
      </div>
    );
  }

  return (
    <div className="results-content custom-scrollbar">
      {isLoading ? (
        <SearchResultsSkeleton />
      ) : error ? (
        <div className="results-empty">
          <Search className="results-empty-icon" />
          <p className="results-empty-text">{error}</p>
        </div>
      ) : invoice ? (
        <button
          onClick={() => onSelectInvoice(invoice)}
          disabled={fueraDePlazo(invoice.timestamp)}
          title={fueraDePlazo(invoice.timestamp)
            ? `Fuera del plazo de devolución de ${DIAS_DEVOLUCION} días`
            : undefined}
          className={`results-book-item results-book-item-stock${fueraDePlazo(invoice.timestamp) ? ' results-book-item-blocked' : ''}`}
        >
          <span className="results-book-title">Ticket {invoice.ticketNumber || invoice.id}</span>
          <span className="results-book-author">
            {invoice.clientName || 'Sin cliente'}
            {invoice.invoiceId && ` • Factura ${invoice.invoiceNumber || invoice.invoiceId}`}
          </span>
          <div className="results-book-info-row">
            <div className="results-book-info-left">
              <span className="results-book-isbn">
                <FileText className="w-3 h-3" />{' '}
                {invoice.items.length} {invoice.items.length === 1 ? 'línea' : 'líneas'}
                {' • '}
                {invoice.timestamp.toLocaleDateString()}
              </span>
              {/*
                Avisa, no impide: el plazo son DIAS_DEVOLUCION dias, pero quien
                decide devolver fuera de plazo es el cajero.
              */}
              {fueraDePlazo(invoice.timestamp) && (
                <div className="results-book-no-stock-badge">
                  <AlertTriangle className="w-3 h-3" />
                  <span>{`Fuera de plazo · ${diasDesdeLaCompra(invoice.timestamp)} días`}</span>
                </div>
              )}
            </div>
            <div className="results-book-info-right">
              <span className="results-book-price">{formatCurrency(invoice.total)}</span>
            </div>
          </div>
        </button>
      ) : (
        <div className="results-empty">
          <Search className="results-empty-icon" />
          <p className="results-empty-text">No se encontró ningún ticket ni factura "{term}"</p>
        </div>
      )}
    </div>
  );
};
